import { Controller } from "stimulus";

let youtubeApiPromise;

function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === "function") previousReady();
      resolve(window.YT);
    };

    if (!document.querySelector("script[data-vsl-youtube-api]")) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.dataset.vslYoutubeApi = "true";
      document.head.appendChild(script);
    }
  });

  return youtubeApiPromise;
}

export default class extends Controller {
  static targets = ["video", "iframe"];

  static values = {
    provider: String,
    pulseUrl: String,
    ctaUrl: String,
    trackingToken: String,
  };

  connect() {
    this.positionSeconds = 0;
    this.durationSeconds = 0;
    this.playing = false;
    this.pulseTimer = null;
    this.boundVisibility = () => this.visibilityChanged();
    document.addEventListener("visibilitychange", this.boundVisibility);

    if (this.providerValue === "upload") this.setupHtmlVideo();
    if (this.providerValue === "vimeo") this.setupVimeo();
    if (this.providerValue === "youtube") this.setupYouTube();
  }

  disconnect() {
    this.stopTracking({ flush: true });
    document.removeEventListener("visibilitychange", this.boundVisibility);
    this.teardownHtmlVideo();
    this.teardownVimeo();
    if (this.youtubePlayer && this.youtubePlayer.destroy) this.youtubePlayer.destroy();
  }

  setupHtmlVideo() {
    if (!this.hasVideoTarget) return;

    this.htmlVideo = this.videoTarget;
    this.boundHtmlPlaying = () => this.startTracking();
    this.boundHtmlStopped = () => this.stopTracking({ flush: true });
    this.boundHtmlMetrics = () => this.updateHtmlMetrics();
    this.htmlVideo.addEventListener("playing", this.boundHtmlPlaying);
    this.htmlVideo.addEventListener("pause", this.boundHtmlStopped);
    this.htmlVideo.addEventListener("waiting", this.boundHtmlStopped);
    this.htmlVideo.addEventListener("ended", this.boundHtmlStopped);
    this.htmlVideo.addEventListener("timeupdate", this.boundHtmlMetrics);
    this.htmlVideo.addEventListener("loadedmetadata", this.boundHtmlMetrics);
    this.updateHtmlMetrics();
    if (!this.htmlVideo.paused) this.startTracking();
  }

  teardownHtmlVideo() {
    if (!this.htmlVideo) return;

    this.htmlVideo.removeEventListener("playing", this.boundHtmlPlaying);
    this.htmlVideo.removeEventListener("pause", this.boundHtmlStopped);
    this.htmlVideo.removeEventListener("waiting", this.boundHtmlStopped);
    this.htmlVideo.removeEventListener("ended", this.boundHtmlStopped);
    this.htmlVideo.removeEventListener("timeupdate", this.boundHtmlMetrics);
    this.htmlVideo.removeEventListener("loadedmetadata", this.boundHtmlMetrics);
  }

  updateHtmlMetrics() {
    if (!this.htmlVideo) return;

    this.positionSeconds = this.htmlVideo.currentTime || 0;
    this.durationSeconds = this.htmlVideo.duration || 0;
  }

  setupVimeo() {
    if (!this.hasIframeTarget || !window.Vimeo || !window.Vimeo.Player) return;

    this.vimeoPlayer = new window.Vimeo.Player(this.iframeTarget);
    this.boundVimeoTime = (data) => {
      this.positionSeconds = data.seconds || 0;
      this.durationSeconds = data.duration || 0;
    };
    this.boundVimeoPlay = () => this.startTracking();
    this.boundVimeoStop = () => this.stopTracking({ flush: true });
    this.boundVimeoBufferEnd = () => {
      this.vimeoPlayer.getPaused().then((paused) => {
        if (!paused) this.startTracking();
      }).catch(() => {});
    };
    this.vimeoPlayer.on("timeupdate", this.boundVimeoTime);
    this.vimeoPlayer.on("play", this.boundVimeoPlay);
    this.vimeoPlayer.on("pause", this.boundVimeoStop);
    this.vimeoPlayer.on("bufferstart", this.boundVimeoStop);
    this.vimeoPlayer.on("bufferend", this.boundVimeoBufferEnd);
    this.vimeoPlayer.on("ended", this.boundVimeoStop);

    Promise.all([
      this.vimeoPlayer.getCurrentTime(),
      this.vimeoPlayer.getDuration(),
      this.vimeoPlayer.getPaused(),
    ]).then(([position, duration, paused]) => {
      this.positionSeconds = position;
      this.durationSeconds = duration;
      if (!paused) this.startTracking();
    }).catch(() => {});
  }

  teardownVimeo() {
    if (!this.vimeoPlayer) return;

    this.vimeoPlayer.off("timeupdate", this.boundVimeoTime);
    this.vimeoPlayer.off("play", this.boundVimeoPlay);
    this.vimeoPlayer.off("pause", this.boundVimeoStop);
    this.vimeoPlayer.off("bufferstart", this.boundVimeoStop);
    this.vimeoPlayer.off("bufferend", this.boundVimeoBufferEnd);
    this.vimeoPlayer.off("ended", this.boundVimeoStop);
  }

  setupYouTube() {
    if (!this.hasIframeTarget) return;

    loadYouTubeApi().then((YT) => {
      if (!this.element.isConnected) return;
      this.youtubePlayer = new YT.Player(this.iframeTarget, {
        events: {
          onReady: () => {
            this.updateYouTubeMetrics();
            if (this.youtubePlayer.getPlayerState() === YT.PlayerState.PLAYING) this.startTracking();
          },
          onStateChange: (event) => {
            this.updateYouTubeMetrics();
            if (event.data === YT.PlayerState.PLAYING) {
              this.startTracking();
            } else if (
              event.data === YT.PlayerState.PAUSED ||
              event.data === YT.PlayerState.BUFFERING ||
              event.data === YT.PlayerState.ENDED
            ) {
              this.stopTracking({ flush: true });
            }
          },
        },
      });
    }).catch(() => {});
  }

  updateYouTubeMetrics() {
    if (!this.youtubePlayer || !this.youtubePlayer.getCurrentTime) return;

    this.positionSeconds = this.youtubePlayer.getCurrentTime() || 0;
    this.durationSeconds = this.youtubePlayer.getDuration() || 0;
  }

  visibilityChanged() {
    if (document.visibilityState !== "visible") {
      this.stopTracking({ flush: true });
      return;
    }

    if (this.providerValue === "upload" && this.htmlVideo && !this.htmlVideo.paused) this.startTracking();
    if (this.providerValue === "vimeo" && this.vimeoPlayer) {
      this.vimeoPlayer.getPaused().then((paused) => {
        if (!paused) this.startTracking();
      }).catch(() => {});
    }
    if (this.providerValue === "youtube" && this.youtubePlayer && window.YT) {
      if (this.youtubePlayer.getPlayerState() === window.YT.PlayerState.PLAYING) this.startTracking();
    }
  }

  startTracking() {
    if (document.visibilityState && document.visibilityState !== "visible") return;

    this.playing = true;
    if (this.pulseTimer) return;
    this.sendPulse();
    this.pulseTimer = setInterval(() => this.sendPulse(), 15000);
  }

  stopTracking({ flush = false } = {}) {
    if (flush && this.pulseTimer) this.sendPulse({ force: true });
    if (this.pulseTimer) clearInterval(this.pulseTimer);
    this.pulseTimer = null;
    this.playing = false;
  }

  sendPulse({ force = false } = {}) {
    if (!force && !this.playing) return;
    if (!force && document.visibilityState && document.visibilityState !== "visible") return;

    if (this.providerValue === "upload") this.updateHtmlMetrics();
    if (this.providerValue === "youtube") this.updateYouTubeMetrics();
    if (!(this.durationSeconds > 0)) return;

    this.post(this.pulseUrlValue, {
      tracking_token: this.trackingTokenValue,
      position_seconds: this.positionSeconds,
      duration_seconds: this.durationSeconds,
    });
  }

  ctaClicked() {
    const clickId = window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.post(this.ctaUrlValue, {
      tracking_token: this.trackingTokenValue,
      click_id: clickId,
    });
  }

  post(url, body) {
    if (!url || !this.trackingTokenValue) return;

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
    fetch(url, {
      method: "POST",
      headers,
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify(body),
    }).catch(() => {});
  }
}
